(function (window, document) {
  'use strict';

  // Tracks the previously active OneTrust groups so we can detect opt-outs.
  let previousGroups = null;

  /**
   * Returns the list of currently active OneTrust group IDs.
   * Example: ["C0001", "C0003"]
   */
  function getActiveGroups() {
    return (window.OnetrustActiveGroups || '')
      .split(',')
      .filter(Boolean);
  }

  /**
   * Returns all current cookie names from document.cookie.
   */
  function getCookieNames() {
    return document.cookie
      .split(';')
      .map(c => c.split('=')[0].trim())
      .filter(Boolean);
  }

  /**
   * Builds a list of possible domains to attempt cookie deletion.
   * Includes hostname and parent domains to handle cookies set at different levels.
   */
  function getCookieDomains() {
    const hostname = window.location.hostname;
    const parts = hostname.split('.');

    const domains = [
      hostname,
      '.' + hostname
    ];

    if (parts.length >= 2) {
      domains.push('.' + parts.slice(-2).join('.'));
    }

    if (parts.length >= 3) {
      domains.push('.' + parts.slice(-3).join('.'));
    }

    return [...new Set(domains)];
  }

  /**
   * Attempts to delete a cookie across multiple paths and domains.
   * Required because cookies may be set at different domain/path combinations.
   */
  function deleteCookie(name) {
    const paths = ['/', window.location.pathname];

    paths.forEach(path => {
      document.cookie = name + '=; Max-Age=0; path=' + path + ';';
      document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=' + path + ';';

      getCookieDomains().forEach(domain => {
        document.cookie = name + '=; Max-Age=0; path=' + path + '; domain=' + domain + ';';
        document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=' + path + '; domain=' + domain + ';';
      });
    });
  }

  /**
   * Returns cookie names that belong to the specified inactive OneTrust groups.
   * Uses OneTrust's domain data (Groups + Hosts) as the source of truth.
   */
  function getCookiesForInactiveGroups(inactiveGroups) {
    const domainData = window.Optanon && window.Optanon.GetDomainData
      ? window.Optanon.GetDomainData()
      : null;

    if (!domainData || !domainData.Groups) {
      return [];
    }

    const cookieNames = [];

    domainData.Groups.forEach(group => {
      if (!inactiveGroups.includes(group.CustomGroupId)) {
        return;
      }

      (group.Cookies || []).forEach(cookie => {
        cookieNames.push(cookie.Name);
      });

      (group.Hosts || []).forEach(host => {
        (host.Cookies || []).forEach(cookie => {
          cookieNames.push(cookie.Name);
        });
      });
    });

    return [...new Set(cookieNames)];
  }

  /**
   * Determines whether a cookie matches a manual override rule.
   * Rules can be exact strings or regex patterns from Drupal config.
   */
  function shouldDeleteManualCookie(name) {
    return getManualRemovalRules().some(rule =>
      rule instanceof RegExp ? rule.test(name) : rule === name
    );
  }

  /**
   * Deletes cookies that:
   * 1. Belong to inactive OneTrust groups
   * 2. Match manual override rules
   */
  function removeInactiveGroupCookies(inactiveGroups) {
    const oneTrustCookiesToDelete = getCookiesForInactiveGroups(inactiveGroups);
    const currentCookies = getCookieNames();

    currentCookies
      .filter(name => {
        return oneTrustCookiesToDelete.some(oneTrustName =>
          oneTrustNameMatchesCookie(oneTrustName, name)
        ) || shouldDeleteManualCookie(name);
      })
      .forEach(deleteCookie);
  }

  /**
   * Handles consent changes by detecting which groups were opted out.
   * Only removes cookies for groups that transitioned from active → inactive.
   */
  function handleConsentChange() {
    const currentGroups = getActiveGroups();

    // First run: establish baseline only.
    if (previousGroups === null) {
      previousGroups = currentGroups;
      return;
    }

    // Identify groups that were turned OFF.
    const inactiveGroups = previousGroups.filter(groupId => {
      return !currentGroups.includes(groupId);
    });

    if (inactiveGroups.length) {
      removeInactiveGroupCookies(inactiveGroups);
    }

    previousGroups = currentGroups;
  }

  /**
   * Parses manual cookie removal rules from Drupal settings.
   * Supports:
   * - Exact names (e.g. "_ga")
   * - Regex patterns (e.g. /^_ga_.+$/)
   */
  function getManualRemovalRules() {
    const raw = window.drupalSettings?.empThirdPartyJs?.onetrust?.manualCookieRemovalPatterns || '';

    return raw
      .split(/\r?\n/)
      .map(line => line.trim())
      .filter(Boolean)
      .map(line => {
        if (line.startsWith('/') && line.lastIndexOf('/') > 0) {
          const lastSlash = line.lastIndexOf('/');
          const pattern = line.slice(1, lastSlash);
          const flags = line.slice(lastSlash + 1);
          return new RegExp(pattern, flags);
        }

        return line;
      });
  }

  /**
   * Matches OneTrust cookie names to actual cookies.
   * Converts placeholder patterns like "_ga_xxxxxxxxxx" into regex.
   */
  function oneTrustNameMatchesCookie(oneTrustName, actualCookieName) {
    if (oneTrustName === actualCookieName) {
      return true;
    }

    const pattern = '^' + oneTrustName
      .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      .replace(/x+/gi, '.+') + '$';

    return new RegExp(pattern).test(actualCookieName);
  }

  /**
   * Initializes the cleanup logic and binds to OneTrust consent events.
   */
  function init() {
    handleConsentChange();

    if (window.OneTrust && window.OneTrust.OnConsentChanged) {
      window.OneTrust.OnConsentChanged(handleConsentChange);
    }

    window.addEventListener('OTConsentApplied', handleConsentChange);
    window.addEventListener('OneTrustGroupsUpdated', handleConsentChange);
  }

  window.empOnetrustOptanon = {
    init: init
  };

})(window, document);
